import { TranslateService } from '@ngx-translate/core';
import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { LocalStorageService, SessionStorageService } from 'ngx-webstorage';

@Component({
  selector: 'app-collapsible',
  templateUrl: './collapsible.component.html',
  styleUrls: ['./collapsible.component.scss']
})
export class CollapsibleComponent implements OnInit
{

  /**
   * Unique storage key for saving the collapsible to local storage.
   */
  @Input() public storageKey: string;

  /**
   * Indicate the default if the key is not found.
   */
  @Input() public defaultCollapsed = false;

  /**
   * Indicate if using session storage. This is set to true only for collapsible using ids.
   * Those will be stored per session only.
   */
  @Input() public useSessionStorage = false;

  /**
   * Event emitter when when switch changes
   */
  @Output() public collapsibleToggle = new EventEmitter<boolean>();
  
  /**
   * Track if advanced mode is enabled.
   */
  public collapsed: boolean;

  /**
   * Storage prefix
   */
  private storagePrefix = 'collapsible-section-';

  /**
   * Localization texts
   */
  private expandText: string;
  private collapseText: string;

  constructor(
    private localStorageService: LocalStorageService,
    private sessionStorageService: SessionStorageService,
    private translate: TranslateService)
  { }

  public ngOnInit(): void
  {
    const tmpCollapsed = this.storageKey !== undefined ? this.retrieve(`${this.storagePrefix}${this.storageKey}`) : null;
    this.collapsed = tmpCollapsed !== null ? tmpCollapsed : this.defaultCollapsed;
    this.collapsibleToggle.emit(this.collapsed);

    this.translate.get(['EXPAND-NAV', 'COLLAPSE-NAV']).subscribe({
      next: (res) =>
      {
        this.expandText = res['EXPAND-NAV'];
        this.collapseText = res['COLLAPSE-NAV'];
      }
    });
  }

  /**
   * Event handler when the collapsed/expanded has changed.
   *
   * @param newValue New switch value.
   */
  public onChange(event: Event): void
  {
    event.stopPropagation();    // Must stop propagation to prevent changing state when clicking on the collapsable icon
    this.applyToggleChangeState();
  }

  /**
   * Toggle change method called when user clicked on the text header.
   *
   * @param newCollapsed Optionnal option to set collapsed from source component.
   */
  public toggleChange(newCollapsed: boolean = undefined): void
  {
    this.collapsed = newCollapsed !== undefined ? newCollapsed : !this.collapsed;
    this.applyToggleChangeState();
  }

  /**
   * Return the title to show
   */
  public getTitle(): string
  {
    return this.collapsed ? this.expandText : this.collapseText;
  }

  private retrieve(key: string): any
  {
    if (this.useSessionStorage)
    {
      return this.sessionStorageService.retrieve(key);
    }
    else
    {
      return this.localStorageService.retrieve(key);
    }
  }

  private store(key: string, value: boolean): void
  {
    if (this.useSessionStorage)
    {
      this.sessionStorageService.store(key, value);
    }
    else
    {
      this.localStorageService.store(key, value);
    }
  }

  private applyToggleChangeState(): void
  {
    if (this.storageKey !== undefined)
    {
      this.store(`${this.storagePrefix}${this.storageKey}`, this.collapsed);
    }
    this.collapsibleToggle.emit(this.collapsed);
  }
}
